import { useState, useEffect, useCallback } from 'react';
import {
  getConfigByCategory,
  createConfigValue,
  updateConfigValue,
  deleteConfigValue,
  ConfigValue
} from '../services/configService';
import { formatDate } from '../utils/dateUtils';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/FormPages.css';
import '../styles/ListPages.css';

interface ConfigValuesEditorProps {
  categoryName: string;
  title: string;
  keyLabel: string;
  keyPlaceholder: string;
  valueLabel: string;
  valuePlaceholder: string;
}

export function ConfigValuesEditor({
  categoryName,
  title,
  keyLabel,
  keyPlaceholder,
  valueLabel,
  valuePlaceholder
}: ConfigValuesEditorProps) {
  const [values, setValues] = useState<ConfigValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [categoryID, setCategoryID] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    configKey: '',
    configValue: '',
    displayOrder: 0
  });

  const loadValues = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getConfigByCategory(categoryName);
      setValues(data);
      if (data.length > 0) {
        setCategoryID(data[0].categoryID);
      }
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load values'));
    } finally {
      setLoading(false);
    }
  }, [categoryName]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!categoryID && !isEditing) {
      setError('Category ID not found. Please reload the page.');
      return;
    }

    try {
      if (isEditing && editingId) {
        await updateConfigValue(editingId, formData);
        setSuccess('Value updated successfully');
      } else if (categoryID) {
        await createConfigValue({
          categoryID: categoryID,
          ...formData
        });
        setSuccess('Value created successfully');
      }

      setFormData({ configKey: '', configValue: '', displayOrder: 0 });
      setIsEditing(false);
      setEditingId(null);
      loadValues();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to save value'));
    }
  };

  const handleEdit = (value: ConfigValue) => {
    setFormData({
      configKey: value.configKey,
      configValue: value.configValue,
      displayOrder: value.displayOrder
    });
    setIsEditing(true);
    setEditingId(value.configValueID);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: number, key: string) => {
    if (!confirm(`Are you sure you want to delete "${key}"? This will hide it from all dropdowns.`)) {
      return;
    }

    try {
      await deleteConfigValue(id);
      setSuccess('Value deleted successfully (soft delete)');
      loadValues();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to delete value'));
    }
  };

  const handleCancel = () => {
    setFormData({ configKey: '', configValue: '', displayOrder: 0 });
    setIsEditing(false);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>
      )}

      {/* Add/Edit Form */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3>{isEditing ? `Edit ${title}` : `Add New ${title}`}</h3>
        <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="configKey">{keyLabel} *</label>
              <input
                type="text"
                id="configKey"
                value={formData.configKey}
                onChange={(e) => setFormData({ ...formData, configKey: e.target.value })}
                placeholder={keyPlaceholder}
                required
              />
              <small>Unique identifier used in the system</small>
            </div>

            <div className="form-group">
              <label htmlFor="configValue">{valueLabel} *</label>
              <input
                type="text"
                id="configValue"
                value={formData.configValue}
                onChange={(e) => setFormData({ ...formData, configValue: e.target.value })}
                placeholder={valuePlaceholder}
                required
              />
              <small>Display label shown to users</small>
            </div>

            <div className="form-group">
              <label htmlFor="displayOrder">Display Order</label>
              <input
                type="number"
                id="displayOrder"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <small>Sort order (0 = first)</small>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" className="button-primary">
              {isEditing ? 'Update Value' : 'Add Value'}
            </button>
            {isEditing && (
              <button type="button" className="button-secondary" onClick={handleCancel}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Values List */}
      <div className="card">
        <h3>Current {title}</h3>
        {loading ? (
          <p>Loading...</p>
        ) : values.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>No values found.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>{keyLabel}</th>
                  <th>{valueLabel}</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {values.map(value => (
                  <tr key={value.configValueID}>
                    <td>{value.displayOrder}</td>
                    <td><code>{value.configKey}</code></td>
                    <td><strong>{value.configValue}</strong></td>
                    <td>{formatDate(value.createdAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(value)}
                          className="button-link"
                          style={{ color: '#5B2C86' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(value.configValueID, value.configKey)}
                          className="button-link"
                          style={{ color: '#D32F2F' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
